import React, { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { DollarOutlined } from '@ant-design/icons';
import { Table as AntdTable, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IRefund } from 'v2/interfaces/reports/refunds';
import { refundsService } from 'v2/services/reports/refunds';

const PartialRefundTag = () => (
  <Tag className='rounded-[14px] border border-[#eee] bg-white px-2 py-0 text-sm text-[#061D37]'>
    <DollarOutlined className='mr-1 text-[#596375]' style={{ fontSize: 14 }} />
    <span className='font-medium'>Reembolso Parcial</span>
  </Tag>
);

export const Table = () => {
  useObservable(() => refundsService.loadRefunds(), []);
  const [refunds] = useObservable(() => refundsService.getRefunds(), []);
  const [isLoading] = useObservable(() => refundsService.isLoading(), []);
  const [pagination] = useObservable(() => refundsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      refundsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
      document.getElementById('refunds-table')?.scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      refundsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('refunds-table')?.scrollIntoView();
    },
    [pagination]
  );

  const partialRefundRowKeys = useMemo(
    () => (refunds || []).filter(refund => refund.partial_refund).map(refund => String(refund.invoice_id)),
    [refunds]
  );

  const columns: ColumnsType<IRefund> = useMemo(
    () => [
      {
        title: 'Fatura',
        dataIndex: 'invoice_id',
        key: 'invoice_id',
        width: 70,
        render: (value: number) => value
      },
      {
        title: 'Produto',
        key: 'product',
        render: (_: unknown, refund) => (
          <>
            <span className='block'>
              <Truncate count={30} final='...'>
                {refund?.product_name || '-'}
              </Truncate>
            </span>
            <span className='block'>({refund?.product_id || '-'})</span>
          </>
        )
      },
      {
        title: 'Pagamento em',
        dataIndex: 'received_at',
        key: 'received_at',
        width: 100,
        render: (value: string) => (
          <>
            <span className='block'>{formatDate(value, 'dd/MM/yyyy')}</span>
            <span className='block'>{formatDate(value, 'HH:mm')}</span>
          </>
        )
      },
      {
        title: 'Reembolso em',
        dataIndex: 'refunded_at',
        key: 'refunded_at',
        width: 100,
        render: (value: string) => (
          <>
            <span className='block'>{formatDate(value, 'dd/MM/yyyy')}</span>
            <span className='block'>{formatDate(value, 'HH:mm')}</span>
          </>
        )
      },
      {
        title: 'Dias Reembolso',
        dataIndex: 'refund_days',
        key: 'refund_days',
        width: 110,
        render: (value: number) => value || 0
      },
      {
        title: 'Motivo',
        dataIndex: 'motive',
        key: 'motive',
        width: 220,
        render: (value: string) => value || '-'
      },
      {
        title: 'Valor da Venda',
        dataIndex: 'total_refunded',
        key: 'total_refunded',
        width: 100,
        align: 'right',
        render: (value: number) => (
          <span className='font-semibold'>
            <FormattedNumber value={value || 0} format='BRL' />
          </span>
        )
      }
    ],
    []
  );

  return (
    <div id='refunds-table' data-testid='refunds-table' className='overflow-x-auto'>
      <AntdTable<IRefund>
        rowKey={record => String(record.invoice_id)}
        columns={columns}
        dataSource={(refunds as IRefund[]) || []}
        loading={!!isLoading}
        scroll={{ x: 900 }}
        pagination={{
          current: pagination?.current_page || 1,
          total: pagination?.total_items || 0,
          pageSize: pagination?.per_page || 10,
          pageSizeOptions: ['10', '25', '50'],
          showSizeChanger: true,
          onChange: page => onChangePage(page),
          onShowSizeChange: (_, size) => onChangePerPage(size)
        }}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: partialRefundRowKeys,
          rowExpandable: record => !!record.partial_refund,
          expandedRowRender: () => <PartialRefundTag />
        }}
      />
    </div>
  );
};
